<script setup lang="ts">
import AuthenticatedLayout from '@/Layouts/AuthenticatedLayout.vue';
import { Head, Link } from '@inertiajs/vue3'
import { library } from "@fortawesome/fontawesome-svg-core"
import { } from "@fal"
import { faCheckCircle, faTimesCircle } from "@fas"

import PageHeading from "@/Components/Headings/PageHeading.vue"
import { capitalize } from "@/Composables/capitalize"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { inject, Ref, ref } from "vue"
import { aikuLocaleStructure } from "@/Composables/useLocaleStructure"
import CountUp from 'vue-countup-v3'
import BackgroundBox from '@/Components/BackgroundBox.vue'
import Icon from '@/Components/Utils/Icon.vue'
import { trans } from 'laravel-vue-i18n'
import Image from '@/Components/Utils/Image.vue'
import { layoutStructure } from '@/Composables/useLayoutStructure'
import Button from '@/Components/Utils/Button.vue'
import TableFeeAdditional from '@/Components/Tables/TableFeeAdditional.vue';
import { routeType } from '@/types/route';

library.add(faCheckCircle, faTimesCircle)

interface FeeAdditionalItem {
    id: number
    date: string | Date
    label: string
    amount: number
}

const props = withDefaults(defineProps<{
    pageHeading?: PageHeadingTS
    title: string
    user: {}
    fee_additional: FeeAdditionalItem[] | Ref<FeeAdditionalItem[]>
    profit_route_update?: routeType
}>(), {
    fee_additional: () => ref([]),
})


const layout = inject('layout', layoutStructure)

const linkdashboard = () => {
    if (props.user?.role === 'super_admin' || props.user?.role === 'admin') {
        return route('master.dashboard')
    } else if (props.user?.role === 'sales' || props.user?.role === 'sales_special') {
        return route('sales.dashboard')
    } else if (props.user?.role === 'operator') {
        return route('manufacture.dashboard')
    }

    return route('home')
}

const isLoadingVisit = ref(false)
// console.log('user', linkdashboard())

const generateQuote = [
    {
        quote: 'Success is not final, failure is not fatal: It is the courage to continue that counts.',
        author: 'Winston S. Churchill'
    },
    {
        quote: 'The only way to do great work is to love what you do.',
        author: 'Steve Jobs'
    },
    {
        quote: 'The best way to predict the future is to create it.',
        author: 'Peter Drucker'
    },
    {
        quote: 'The only limit to our realization of tomorrow will be our doubts of today.',
        author: 'Franklin D. Roosevelt'
    },
    {
        quote: 'The best preparation for tomorrow is doing your best today.',
        author: 'H. Jackson Brown Jr.'
    },
    {
        quote: 'The future belongs to those who believe in the beauty of their dreams.',
        author: 'Eleanor Roosevelt'
    },
    {
        quote: 'The only thing we have to fear is fear itself.',
        author: 'Franklin D. Roosevelt'
    },
    {
        quote: 'The greatest glory in living lies not in never falling, but in rising every time we fall.',
        author: 'Nelson Mandela'
    },
    {
        quote: 'The way to get started is to quit talking and begin doing.',
        author: 'Walt Disney'
    },
    {
        quote: 'Don’t watch the clock; do what it does. Keep going.',
        author: 'Sam Levenson'
    },
    {
        quote: 'The best and most beautiful things in the world cannot be seen or even touched - they must be felt with the heart.',
        author: 'Helen Keller'
    },
    {
        quote: 'It does not matter how slowly you go as long as you do not stop.',
        author: 'Confucius'
    },
    {
        quote: 'Believe you can and you’re halfway there.',
        author: 'Theodore Roosevelt'
    },
    {
        quote: 'The only limit to our realization of tomorrow will be our doubts of today.',
        author: 'Franklin D. Roosevelt'
    },
    {
        quote: 'The best preparation for tomorrow is doing your best today.',
        author: 'H. Jackson Brown Jr.'
    },
    {
        quote: 'The future belongs to those who believe in the beauty of their dreams.',
        author: 'Eleanor Roosevelt'
    },
    {
        quote: 'The only thing we have to fear is fear itself.',
        author: 'Franklin D. Roosevelt'
    }
]

const index = Math.floor(Math.random() * generateQuote.length)
</script>

<template>

    <Head title="Dashboard" />

    <div class="relative isolate pt-5">
        <svg class="absolute inset-0 -z-10 size-full stroke-gray-200 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
            aria-hidden="true">
            <defs>
                <pattern id="83fd4e5a-9d52-42fc-97b6-718e5d7ee527" width="200" height="200" x="50%" y="-1"
                    patternUnits="userSpaceOnUse">
                    <path d="M100 200V.5M.5 .5H200" fill="none" />
                </pattern>
            </defs>
            <svg x="50%" y="-1" class="overflow-visible fill-gray-50">
                <path
                    d="M-100.5 0h201v201h-201Z M699.5 0h201v201h-201Z M499.5 400h201v201h-201Z M-300.5 600h201v201h-201Z"
                    stroke-width="0" />
            </svg>
            <rect width="100%" height="100%" stroke-width="0" fill="url(#83fd4e5a-9d52-42fc-97b6-718e5d7ee527)" />
        </svg>

        <div class="mx-auto max-w-4xl px-6 py-24 sm:py-8 lg:flex lg:items-center lg:gap-x-10 lg:px-8 lg:py-20">
            <div class="mx-auto max-w-4xl lg:mx-0 lg:flex-auto">
                
                <h1 class="mt-10 text-pretty text-5xl tracking-tight  sm:text-7xl">
                    Hello, <span class="font-semibold border-b-4 border-sky-400 px-2">{{ props.user.name }}</span>
                </h1>

                <p class="mt-14 text-pretty text-lg text-gray-500 sm:text-xl/8">
                    <span>"{{ generateQuote[index].quote }}"</span> <br><br>
                    <span class="font-semibold">- {{ generateQuote[index].author }} -</span>
                </p>
                <div class="mt-10 flex items-center gap-x-6">
                    <Link :href="linkdashboard()" @start="() => isLoadingVisit = true" @finish="() => isLoadingVisit = false">
                        <Button size="xl" :loading="isLoadingVisit" label="Dashboard">
                        </Button>
                    </Link>

                </div>
            </div>

        </div>

        <TableFeeAdditional
            v-if="layout?.auth?.role === 'super_admin'"
            class="mt-12 mx-auto w-full max-w-7xl px-6 lg:px-8"
            :feeAdditional="fee_additional"
            :routeUpdate="profit_route_update"
        />
    </div>


    <!-- <AuthenticatedLayout>
        <template #header>
            <h2
                class="text-xl font-semibold leading-tight text-gray-800"
            >
                Dashboard
            </h2>
        </template>

<div class="py-12">
    <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="overflow-hidden bg-white shadow-sm sm:rounded-lg">
            <div class="p-6 ">
                You're logged in!
            </div>
        </div>
    </div>
</div>
</AuthenticatedLayout> -->
</template>
